import { bus, Events } from '@/core/events';

/** Canonical route. Order is the contract; each flag fires exactly once. */
export const STORY_FLAGS = [
  'TITLE',
  'LETTER_OPEN',
  'MEMORY_STORM',
  'MEMORY_ISLAND_RITUAL',
  'MEMORY_BLACK_WATER',
  'MEMORY_FAREWELL',
  'MEMORY_SWORD_TRAINING',
  'LETTER_SENT',
  'PROPHECY_REVEAL',
  'HARBOR_FREE_ROAM',
  'MERCHANT_TURTLE_EGG',
  'RAIN_APPROACHES',
  'INN_ARGUMENT',
  'OMEN_END',
] as const;
export type StoryFlagId = (typeof STORY_FLAGS)[number];

export function isStoryFlag(x: string): x is StoryFlagId {
  return (STORY_FLAGS as readonly string[]).includes(x);
}
export function flagIndex(flag: StoryFlagId): number {
  return STORY_FLAGS.indexOf(flag);
}

export interface StorySnapshot { flags: StoryFlagId[]; marks: string[] }

/**
 * Main-line state machine. `advance` only accepts the next flag; `jumpTo` (save/debug) fills in
 * every earlier flag. Optional exploration marks (`seen:candles`, `talked:sailor-1`) never gate the route.
 */
export class StoryState {
  private readonly set = new Set<StoryFlagId>();
  private readonly markSet = new Set<string>();
  private readonly emit: boolean;

  constructor(snapshot?: Partial<StorySnapshot>, opts: { emit?: boolean } = {}) {
    this.emit = opts.emit ?? true;
    if (snapshot?.flags) for (const f of snapshot.flags) if (isStoryFlag(f)) this.set.add(f);
    if (snapshot?.marks) for (const m of snapshot.marks) this.markSet.add(m);
    // Normalise: a set of flags must be a prefix of the route.
    const idx = this.currentIndex();
    this.set.clear();
    for (let i = 0; i <= idx; i++) this.set.add(STORY_FLAGS[i] as StoryFlagId);
  }

  /** Index of the highest flag set; -1 if none. */
  currentIndex(): number {
    let idx = -1;
    STORY_FLAGS.forEach((f, i) => { if (this.set.has(f)) idx = i; });
    return idx;
  }
  current(): StoryFlagId | null {
    const i = this.currentIndex();
    return i < 0 ? null : (STORY_FLAGS[i] as StoryFlagId);
  }
  next(): StoryFlagId | null {
    const i = this.currentIndex() + 1;
    return i >= STORY_FLAGS.length ? null : (STORY_FLAGS[i] as StoryFlagId);
  }
  has(flag: StoryFlagId): boolean { return this.set.has(flag); }
  isComplete(): boolean { return this.has('OMEN_END'); }

  /** Returns true only when the flag was newly set as the legal next step. */
  advance(flag: StoryFlagId): boolean {
    if (this.set.has(flag)) return false;
    if (this.next() !== flag) {
      console.warn(`[story] rejected out-of-order flag ${flag}; next is ${this.next()}`);
      return false;
    }
    this.set.add(flag);
    if (this.emit) bus.emitTyped(Events.STORY_FLAG, { flag, index: flagIndex(flag) });
    return true;
  }

  /** Debug/save restore: set all flags up to and including `flag`. */
  jumpTo(flag: StoryFlagId): void {
    const target = flagIndex(flag);
    for (let i = 0; i <= target; i++) this.set.add(STORY_FLAGS[i] as StoryFlagId);
    for (let i = target + 1; i < STORY_FLAGS.length; i++) this.set.delete(STORY_FLAGS[i] as StoryFlagId);
    if (this.emit) bus.emitTyped(Events.STORY_JUMP, { flag });
  }

  mark(id: string): boolean {
    if (this.markSet.has(id)) return false;
    this.markSet.add(id);
    return true;
  }
  hasMark(id: string): boolean { return this.markSet.has(id); }

  snapshot(): StorySnapshot {
    return { flags: STORY_FLAGS.filter((f) => this.set.has(f)), marks: [...this.markSet] };
  }
  reset(): void { this.set.clear(); this.markSet.clear(); }
}
